import React, { useState, useEffect } from "react"
import Select from 'react-select'
import { DateTime } from 'luxon'
import { DateRangePicker } from "../../../../../../mogul/components/DashboardGraph"
import useAvailablePassareOrganizations from "../../../../../../mogul/hooks/useAvailablePassareOrganizations"
import { getPassareBranches } from "../../../../../../mogul/core/_requests"

export interface PassareFilterObject {
    selectedPassareOrganizationUniqueIds: string[],
    selectedBranchNames?: string[],
    onlyUnSyncedWith360: boolean,
    onlySyncedWith360?: boolean,
    showAllLeadsEvenIfLocationIsIgnored: boolean,
    showAllLeadsRegardlessOfContactQuality?: boolean,
    startingUnixTimestamp?: number,
    endingUnixTimestamp?: number
}

export default function FilterPassareAcquaintancesTable({
    onCancel,
    onReset,
    currentFilterObject,
    onApplyFilter,
    isFilterChild = false
}: {
    onCancel: () => void,
    onReset: () => void,
    currentFilterObject: PassareFilterObject | null,
    onApplyFilter: (filterObject: PassareFilterObject) => void,
    isFilterChild?: boolean
}) {
    const [selectedPassareOrganizationUniqueIds, setSelectedPassareOrganizationUniqueIds] = useState<string[]>(
        currentFilterObject ? currentFilterObject.selectedPassareOrganizationUniqueIds : []
    )
    const [selectedBranchNames, setSelectedBranchNames] = useState<string[]>(
        currentFilterObject?.selectedBranchNames || []
    )
    const [onlyUnSyncedWith360, setOnlyUnSyncedWith360] = useState(
        currentFilterObject ? currentFilterObject.onlyUnSyncedWith360 : false
    )
    const [onlySyncedWith360, setOnlySyncedWith360] = useState(
        currentFilterObject ? currentFilterObject.onlySyncedWith360 || false : false
    )
    const [showAllLeadsEvenIfLocationIsIgnored, setShowAllLeadsEvenIfLocationIsIgnored] = useState(
        currentFilterObject ? currentFilterObject.showAllLeadsEvenIfLocationIsIgnored : false
    )
    const [showAllLeadsRegardlessOfContactQuality, setShowAllLeadsRegardlessOfContactQuality] = useState(
        currentFilterObject ? currentFilterObject.showAllLeadsRegardlessOfContactQuality || false : false
    )
    const [startingUnixTimestamp, setStartingUnixTimestamp] = useState(
        currentFilterObject?.startingUnixTimestamp ||
        Math.floor(DateTime.local().setZone('America/Chicago').minus({ days: 365 }).startOf('day').toSeconds())
    )
    const [endingUnixTimestamp, setEndingUnixTimestamp] = useState(
        currentFilterObject?.endingUnixTimestamp ||
        Math.floor(DateTime.local().setZone('America/Chicago').toSeconds())
    )

    const {
        availablePassareOrganizations,
        loading: loadingPassareOrganizations,
        errorMessage: errorLoadingPassareOrganizations,
    } = useAvailablePassareOrganizations()
    
    // Local state for branch names
    const [availableBranchNames, setAvailableBranchNames] = useState<string[]>([])
    const [loadingBranches, setLoadingBranches] = useState(false)
    
    // Fetch branches when selected organizations change - uses efficient dedicated endpoint
    useEffect(() => {
        const fetchBranches = async () => {
            if (!selectedPassareOrganizationUniqueIds.length) {
                setAvailableBranchNames([])
                return
            }

            setLoadingBranches(true)
            try {
                const branches = await getPassareBranches(selectedPassareOrganizationUniqueIds)
                setAvailableBranchNames(branches)
            } catch (error) {
                console.error('Error fetching branches:', error)
                setAvailableBranchNames([])
            } finally {
                setLoadingBranches(false)
            }
        }

        fetchBranches()
    }, [selectedPassareOrganizationUniqueIds.join(',')])

    return (
        <div>
            {!isFilterChild && <>
                <div className='px-7 py-5'>
                    <div className='fs-5 text-dark fw-bolder'>Filter List</div>
                </div>
                <div className='separator border-gray-200'></div>
            </>}
            <div className={isFilterChild ? `` : 'px-7 py-5'}>
                <div className='mb-5'>
                    <label className='form-label fw-bold'>
                        Passare Organization
                    </label>
                    {errorLoadingPassareOrganizations && (
                        <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: 14 }}>
                            Error loading organizations: {errorLoadingPassareOrganizations}
                        </div>
                    )}
                    <div>
                        <Select
                            options={[
                                { value: '', label: '-All Passare Organizations-' },
                                ...availablePassareOrganizations.map((option) => ({
                                    value: `${option.unique_identifier}`,
                                    label: option.organization_name
                                }))
                            ]}
                            onChange={(selectedOption) => {
                                setSelectedPassareOrganizationUniqueIds(selectedOption ? selectedOption.map((x) => x.value) : [])
                            }}
                            isMulti
                            filterOption={(option, searchValue) => {
                                const words = searchValue.toLowerCase().split(' ');
                                return words.every(word => option.label.toLowerCase().includes(word));
                            }}
                            value={availablePassareOrganizations
                                .filter(x => (selectedPassareOrganizationUniqueIds || []).includes(`${x.unique_identifier}`))
                                .map((option) => ({
                                    value: `${option.unique_identifier}`,
                                    label: option.organization_name
                                }))
                            }
                            isDisabled={loadingPassareOrganizations || isFilterChild}
                        />
                    </div>
                </div>
                {selectedPassareOrganizationUniqueIds.length > 0 && (
                    <div className='mb-5'>
                        <label className='form-label fw-bold'>
                            Branch Name
                            {loadingBranches && <span className="ms-2 spinner-border spinner-border-sm"></span>}
                        </label>
                        {availableBranchNames.length > 0 ? (
                            <div>
                                <Select
                                    options={[
                                        { value: '', label: '-All Branches-' },
                                        ...availableBranchNames.map((branch) => ({
                                            value: branch,
                                            label: branch === '__EMPTY__' ? '(No Branch Specified)' : branch
                                        }))
                                    ]}
                                    onChange={(selectedOption) => {
                                        setSelectedBranchNames(selectedOption ? selectedOption.map((x) => x.value).filter(v => v !== '') : [])
                                    }}
                                    isMulti
                                    value={selectedBranchNames.map((branch) => ({
                                        value: branch,
                                        label: branch === '__EMPTY__' ? '(No Branch Specified)' : branch
                                    }))
                                    }
                                    isDisabled={isFilterChild || loadingBranches}
                                    placeholder="Select branches..."
                                />
                            </div>
                        ) : (
                            <div className="text-muted">
                                {loadingBranches ? 'Loading branches...' : 'No branches found for selected organizations'}
                            </div>
                        )}
                    </div>
                )}
                {!isFilterChild && <>
                    <div className='mb-5'>
                        <label className='form-label fw-bold'>Only those un-synced with 360:</label>
                        <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                name='notifications'
                                onChange={(e) => {
                                    setOnlyUnSyncedWith360(e.target.checked)
                                    if (e.target.checked) {
                                        setOnlySyncedWith360(false)
                                    }
                                }}
                                checked={onlyUnSyncedWith360}
                            />
                            <label className='form-check-label'>Enabled</label>
                        </div>
                    </div>
                    <div className='mb-5'>
                        <label className='form-label fw-bold'>Only those synced with 360:</label>
                        <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                name='syncedWith360'
                                onChange={(e) => {
                                    setOnlySyncedWith360(e.target.checked)
                                    if (e.target.checked) {
                                        setOnlyUnSyncedWith360(false)
                                    }
                                }}
                                checked={onlySyncedWith360}
                            />
                            <label className='form-check-label'>Enabled</label>
                        </div>
                    </div>
                    <div className='mb-5'>
                        <label className='form-label fw-bold'>Show all leads even if location is ignored:</label>
                        <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                name='showAllLeads'
                                onChange={(e) => setShowAllLeadsEvenIfLocationIsIgnored(e.target.checked)}
                                checked={showAllLeadsEvenIfLocationIsIgnored}
                            />
                            <label className='form-check-label'>Enabled</label>
                        </div>
                    </div>
                    <div className='mb-5'>
                        <label className='form-label fw-bold'>Show all leads regardless of contact quality:</label>
                        <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                name='showAllLeadsRegardlessOfContactQuality'
                                onChange={(e) => setShowAllLeadsRegardlessOfContactQuality(e.target.checked)}
                                checked={showAllLeadsRegardlessOfContactQuality}
                            />
                            <label className='form-check-label'>Enabled</label>
                        </div>
                        <div className="form-text">
                            By default, only leads with email/phone and valid deceased person info are shown. 
                            Enable this to see all 96 leads including those with incomplete contact information.
                        </div>
                    </div>
                </>}
                <div className="mb-5">
                    <label className='form-label fw-bold'>Date Range</label>
                    <div style={{ maxWidth: 420 }}>
                        <DateRangePicker
                            startingUnixTimestamp={startingUnixTimestamp}
                            setStartingUnixTimestamp={setStartingUnixTimestamp}
                            endingUnixTimestamp={endingUnixTimestamp}
                            setEndingUnixTimestamp={setEndingUnixTimestamp}
                            hideUpdateButton
                            disabled={!!isFilterChild}
                        />
                    </div>
                </div>
                {isFilterChild && <div className="mb-5">
                    <div className="alert alert-info py-2 px-3 mb-0" style={{ fontSize: 14 }}>
                        <strong>Note:</strong> Filtering must be changed before exporting, to ensure the correct leads are exported.
                    </div>
                </div>}
                {!isFilterChild && <>
                    <div className='d-flex justify-content-end'>
                        <button
                            type='button'
                            className='btn btn-sm btn-light btn-active-light-primary me-2'
                            onClick={onReset}
                        >
                            Reset
                        </button>
                        <button
                            type='button'
                            className='btn btn-sm btn-light btn-active-light-primary me-2'
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            className='btn btn-sm btn-primary'
                            onClick={() => onApplyFilter({
                                selectedPassareOrganizationUniqueIds,
                                selectedBranchNames,
                                onlyUnSyncedWith360,
                                onlySyncedWith360,
                                showAllLeadsEvenIfLocationIsIgnored,
                                showAllLeadsRegardlessOfContactQuality,
                                startingUnixTimestamp,
                                endingUnixTimestamp
                            })}
                        >
                            Apply
                        </button>
                    </div>
                </>}
            </div>
        </div>
    )
}
